import React, { useEffect, useMemo, useState } from 'react'

import { ampli } from 'ampli'
import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import Toast from 'components/Common/Toast'
import { ASSESSMENTS, ROLES } from 'config'
import Button from 'components/Common/Button'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import NextPlays from 'components/Owner/Dashboard/NextPlays'
import OwnerNavigationCards from './OwnerNavigationCards.tsx'
import { AttestationStatus } from '__generated__/api-types-and-hooks'
import { launchUsetifulIfNeeded, navigateTo, showToast } from 'utils/helper'
import { IRecommendationCardProps } from 'components/Common/RecommendationCard'
import TimezoneModal from 'components/Common/UserTimeZone/TimeZoneModal/TimeZoneModal.component'
import ProfileSection from 'components/Dashboard/Common/ProfileSection/ProfileSection.component'
import { checkTimeZone } from 'utils/dates'
import FutureEvents from 'components/Dashboard/Advisor/FutureEvents/FutureEvents.component'

export default function Dashboard({
  toastData,
  currentUser,
  businessName,
  recommendations,
  businessProfile,
  chatUnreadMessageCount,
  updateUserProfileAction,
  isLoadingRecommendations,
  getUserBusinessProfileAction,
  getRecommendedPlaysAssessments,
  resetRecommendedPlaysAssessments,
  getAssessmentResponseByQuestionAction,
}) {
  const [closeToast, setCloseToast] = useState(false)
  const [showTimezoneModal, setShowTimezoneModal] = useState(false)

  const notificationsEnabled = useFeatureFlag(currentUser?.id, 'release-attestations-rollout')
  useEffect(
    () => {
      getUserBusinessProfileAction({ userId: currentUser?.id })
      getAssessmentResponseByQuestionAction({ question: 'isIndustry', type: 'initial' })
      if (checkTimeZone(currentUser, moment.tz.guess())) {
        setShowTimezoneModal(true)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAssessmentResponseByQuestionAction, getUserBusinessProfileAction, currentUser]
  )
  useEffect(() => {
    getRecommendedPlaysAssessments()
    return () => resetRecommendedPlaysAssessments()
    // eslint-disable-next-line
  }, [getRecommendedPlaysAssessments])

  useEffect(() => {
    if (window.location.href.includes('?event=signing_complete')) {
      updateUserProfileAction({
        attestationStatus: AttestationStatus.Signed,
      })
      ampli.monthlyImpactReportStatusChanged({
        userId: businessProfile?.id,
        status: AttestationStatus.Signed,
      })
      navigateTo(ROLES.BUSINESS_OWNER, 'dashboard')
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!isEmpty(currentUser?.id)) {
      launchUsetifulIfNeeded()
    }
    // eslint-disable-next-line
  }, [currentUser])

  const onTimezoneModalClose = async () => {
    updateUserProfileAction({
      interimTimezone: moment.tz.guess(),
    })
    setShowTimezoneModal(false)
    await launchUsetifulIfNeeded()
  }

  /** Returns the first 4 recommended plays/assessments in a format usable by the NextPlays component */
  const parsedRecommendations: IRecommendationCardProps[] = useMemo(() => {
    return recommendations
      .map((recommendation) => {
        const category = recommendation?.meta?.type as 'play' | 'assessment'

        return category === 'play'
          ? {
              category,
              businessTopic:
                ASSESSMENTS[recommendation?.recommendedBy?.type]?.category ||
                recommendation?.recommendedBy ||
                '',
              title: recommendation?.description || '',
              id: recommendation.id,
              level: ASSESSMENTS[recommendation?.meta?.assessmentType]?.level || '',
            }
          : {
              category,
              businessTopic: ASSESSMENTS[recommendation.id]?.category,
              title: ASSESSMENTS[recommendation.id]?.title,
              id: recommendation.id,
            }
      })
      .filter((_, idx) => {
        return idx < 4
      })
  }, [recommendations])
  return (
    <div className="w-full max-w-full lg:max-w-[1450px] relative">
      <div className="absolute w-full ">
        {showToast(toastData, currentUser, closeToast) && notificationsEnabled && (
          <Toast
            variant={toastData?.variant}
            showCloseButton={true}
            onClose={() => setCloseToast(true)}
          >
            {toastData?.infoText}
            <Button
              className="!font-normal !border-b !border-b-primary-text !text-primary-text"
              variant="text"
              label="View Statement"
              onClick={() => {
                navigateTo(ROLES.BUSINESS_OWNER, 'attestation')
                ampli.monthlyImpactReportViewedFromToast({
                  userName: currentUser.firstName + ' ' + currentUser.lastName,
                  businessName: businessName,
                  toastContent: toastData?.infoText,
                })
              }}
            />
          </Toast>
        )}
      </div>
      <ProfileSection
        firstName={currentUser.firstName}
        lastName={currentUser.lastName}
        businessName={businessProfile.businessName}
        profileImgSrc={businessProfile?.logo?.url}
      />

      <OwnerNavigationCards chatUnreadMessageCount={chatUnreadMessageCount} />
      <div className="flex flex-col gap-8 mt-11 sm:mt-[81px] w-full max-w-full lg:flex-row">
        <NextPlays
          recommendations={parsedRecommendations}
          isLoadingRecommendations={isLoadingRecommendations}
        />
        <FutureEvents
          calendarEventViewPreference={currentUser.calendarEventViewPreference}
          role={currentUser.roles}
        />
      </div>

      {showTimezoneModal ? (
        <TimezoneModal
          onClose={onTimezoneModalClose}
          setShowTimezoneModal={setShowTimezoneModal}
          updateUserProfileAction={updateUserProfileAction}
          defaultTimezone={currentUser?.timezone}
        ></TimezoneModal>
      ) : (
        <></>
      )}
    </div>
  )
}
